/**
 * 物料二维码标签：待盘点物料、盘点、条码追溯共用。
 * 70mm 宽 × 50mm 高，8 点/mm；上留 5mm、下留 8mm。
 * 顶部大字物料名称，左侧大字编码和明细，右侧大二维码。
 */
import QRCode from "qrcode";
import { printCanvas, connectPrinter } from "./btPrinter";
export { isSupported, getUnsupportedReason } from "./btPrinter";

// ================================================================
//  布局常量
// ================================================================
var LABEL_WIDTH_MM = 70;
var LABEL_HEIGHT_MM = 50;
var DOTS_PER_MM = 8;
var W = LABEL_WIDTH_MM * DOTS_PER_MM;
var H = LABEL_HEIGHT_MM * DOTS_PER_MM;
export const MATERIAL_LABEL_SIZE_TEXT = LABEL_WIDTH_MM + " × " + LABEL_HEIGHT_MM + " mm";

var MARGIN = 16;
var CONTENT_TOP = 5 * DOTS_PER_MM;
var CONTENT_BOTTOM = H - 8 * DOTS_PER_MM;
var LEFT_WIDTH = 276;
var QR_AREA = 232;
var QR_X = W - MARGIN - QR_AREA;
var QUIET_MODULES = 4;

// ================================================================
//  工具函数
// ================================================================

function fontOf(size, weight, latin) {
  return (weight || 700) + " " + size + (latin
    ? 'px Arial, sans-serif'
    : 'px "Microsoft YaHei", "PingFang SC", sans-serif');
}

function textOf(value) {
  return value === null || value === undefined || value === "" ? "-" : String(value);
}

/** 按实际墨迹对齐，避免不同设备字体挤占上下留白。 */
function inkMetrics(ctx, text, size) {
  var measured = ctx.measureText(text);
  var ascent = Number.isFinite(measured.actualBoundingBoxAscent) ? measured.actualBoundingBoxAscent : size * 0.8;
  var descent = Number.isFinite(measured.actualBoundingBoxDescent) ? measured.actualBoundingBoxDescent : size * 0.2;
  return { ascent: ascent, height: ascent + descent };
}

function drawText(ctx, text, x, top, size, weight, latin) {
  ctx.font = fontOf(size, weight, latin);
  var ink = inkMetrics(ctx, text, size);
  if (top < CONTENT_TOP || top + ink.height > CONTENT_BOTTOM + 0.01) {
    throw new Error("标签文字过长，无法在 70 × 50 mm 标签内排版");
  }
  ctx.fillText(text, x, top + ink.ascent);
  return top + ink.height;
}

/** 按指定宽度换行，保留输入中的换行符及完整字符。 */
function wrapText(ctx, text, maxW, size) {
  ctx.font = fontOf(size);
  var lines = [];
  var cur = "";
  Array.from(text.replace(/\r\n?/g, "\n")).forEach(function(character) {
    if (character === "\n") {
      lines.push(cur);
      cur = "";
    } else if (cur && ctx.measureText(cur + character).width > maxW) {
      lines.push(cur);
      cur = character;
    } else {
      cur += character;
    }
  });
  lines.push(cur);
  return lines;
}

/** 名称和规格最多两行，编码完整显示，不能使用省略号。 */
function limitLines(ctx, lines, maxLines, maxW) {
  if (lines.length > maxLines) {
    lines = lines.slice(0, maxLines);
    var last = Array.from(lines[maxLines - 1]);
    while (last.length && ctx.measureText(last.join("") + "…").width > maxW) {
      last.pop();
    }
    lines[maxLines - 1] = last.join("") + "…";
  }
  return lines;
}

function fitMetadata(ctx, text, maxW, initialSize) {
  var size = initialSize;
  ctx.font = fontOf(size);
  while (size > 16 && ctx.measureText(text).width > maxW) {
    ctx.font = fontOf(--size);
  }
  if (ctx.measureText(text).width > maxW) {
    var characters = Array.from(text);
    while (characters.length && ctx.measureText(characters.join("") + "…").width > maxW) characters.pop();
    text = characters.join("") + "…";
  }
  return { text: text, size: size };
}

/** 从已展示的预览图读取原始画布，预览和打印使用同一张图片。 */
function canvasFromPreview(url) {
  return new Promise(function(resolve, reject) {
    if (typeof url !== "string" || url.indexOf("data:image/png;base64,") !== 0) {
      reject(new Error("打印预览无效，请重新生成预览"));
      return;
    }
    var img = new Image();
    img.onload = function() {
      if (img.naturalWidth !== W || img.naturalHeight !== H) {
        reject(new Error("打印预览尺寸不符，请重新生成 " + MATERIAL_LABEL_SIZE_TEXT + " 标签"));
        return;
      }
      var canvas = document.createElement("canvas");
      canvas.width = W;
      canvas.height = H;
      var ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("当前浏览器无法读取打印预览"));
        return;
      }
      ctx.drawImage(img, 0, 0);
      resolve(canvas);
    };
    img.onerror = function() {
      reject(new Error("打印预览加载失败，请重新生成预览"));
    };
    img.src = url;
  });
}

// ================================================================
//  Canvas 渲染
// ================================================================

/**
 * @param {Object} d - { matnm, specnm, code, stknm, plcnm }
 * @returns {Promise<HTMLCanvasElement>}
 */
function renderLabelCanvas(d) {
  return new Promise(function(resolve) {
    var code = d && d.code !== null && d.code !== undefined ? String(d.code) : "";
    if (!code.trim()) throw new Error("缺少物料编码，无法生成二维码");

    var canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    var ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("当前浏览器无法生成打印预览");

    var qr = QRCode.create(code, { errorCorrectionLevel: "M" });
    var moduleSize = Math.floor(QR_AREA / (qr.modules.size + QUIET_MODULES * 2));
    if (moduleSize < 4) throw new Error("物料编码过长，二维码无法在此标签上清晰打印");
    var qrArea = (qr.modules.size + QUIET_MODULES * 2) * moduleSize;
    var qrX = QR_X + Math.floor((QR_AREA - qrArea) / 2);

    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, W, H);
    ctx.save();
    ctx.beginPath();
    ctx.rect(MARGIN, CONTENT_TOP, W - MARGIN * 2, CONTENT_BOTTOM - CONTENT_TOP);
    ctx.clip();
    ctx.fillStyle = "#000";
    ctx.textBaseline = "alphabetic";

    // 名称独占顶行；长名称降低字号，最多两行。
    drawText(ctx, "物料名称", MARGIN, CONTENT_TOP, 18, 400);
    var nameSize = 40;
    var nameLines = wrapText(ctx, textOf(d.matnm), W - MARGIN * 2, nameSize);
    if (nameLines.length > 1) {
      nameSize = 32;
      nameLines = wrapText(ctx, textOf(d.matnm), W - MARGIN * 2, nameSize);
    }
    if (nameLines.length > 1) {
      nameSize = 24;
      nameLines = wrapText(ctx, textOf(d.matnm), W - MARGIN * 2, nameSize);
    }
    nameLines = limitLines(ctx, nameLines, 2, W - MARGIN * 2);
    var nameBottom = 0;
    nameLines.forEach(function(line, index) {
      nameBottom = drawText(ctx, line, MARGIN,
        nameLines.length > 1 ? 60 + index * 26 : 64, nameSize, 700);
    });

    var codeCaptionBottom = drawText(ctx, "物料编码", MARGIN, Math.max(106, nameBottom + 6), 16, 400);
    var codeSize = 42;
    ctx.font = fontOf(codeSize, 700, true);
    while (codeSize > 28 && ctx.measureText(code).width > LEFT_WIDTH) {
      ctx.font = fontOf(--codeSize, 700, true);
    }
    if (ctx.measureText(code).width > LEFT_WIDTH) {
      throw new Error("物料编码过长，无法在 70 × 50 mm 标签上完整显示");
    }
    var codeBottom = drawText(ctx, code, MARGIN, codeCaptionBottom + 8, codeSize, 700, true);

    var specCaptionBottom = drawText(ctx, "规格描述", MARGIN, Math.max(175, codeBottom + 8), 16, 400);
    var specLines = wrapText(ctx, textOf(d.specnm), LEFT_WIDTH, 22);
    specLines = limitLines(ctx, specLines, 2, LEFT_WIDTH);
    var specBottom = 0;
    specLines.forEach(function(line, index) {
      specBottom = drawText(ctx, line, MARGIN, specCaptionBottom + 10 + index * 26, 22, 700);
    });

    // 时间固定在内容区底部；缩短长字段的行间空白，不占用下方 8mm 留白。
    var now = new Date();
    function pad(n) { return String(n).padStart(2, "0"); }
    var timestamp = "打印时间：" + now.getFullYear() + "-" + pad(now.getMonth() + 1) + "-" + pad(now.getDate()) +
      " " + pad(now.getHours()) + ":" + pad(now.getMinutes());
    ctx.font = fontOf(14, 400);
    var timeTop = CONTENT_BOTTOM - inkMetrics(ctx, timestamp, 14).height;
    var warehouse = fitMetadata(ctx, textOf(d.stknm), LEFT_WIDTH - 60, 22);
    var location = fitMetadata(ctx, textOf(d.plcnm).trim() || "-", LEFT_WIDTH - 60, 24);

    function rowHeight(value, caption) {
      ctx.font = fontOf(value.size);
      var valueHeight = inkMetrics(ctx, value.text, value.size).height;
      ctx.font = fontOf(18, 400);
      return Math.max(valueHeight, 2 + inkMetrics(ctx, caption, 18).height);
    }
    var warehouseHeight = rowHeight(warehouse, "仓库：");
    var locationHeight = rowHeight(location, "仓位：");
    var warehouseTop = Math.max(258, specBottom + 8);
    var locationTop = Math.max(288, warehouseTop + warehouseHeight + 8);
    if (locationTop + locationHeight + 6 > timeTop) {
      warehouseTop = specBottom + 4;
      locationTop = warehouseTop + warehouseHeight + 4;
    }
    if (locationTop + locationHeight + 4 > timeTop) {
      throw new Error("标签内容过长，无法保留上下纸张边距");
    }
    drawText(ctx, "仓库：", MARGIN, warehouseTop + 2, 18, 400);
    drawText(ctx, warehouse.text, MARGIN + 60, warehouseTop, warehouse.size, 700);
    drawText(ctx, "仓位：", MARGIN, locationTop + 2, 18, 400);
    drawText(ctx, location.text, MARGIN + 60, locationTop, location.size, 700);
    drawText(ctx, timestamp, MARGIN, timeTop, 14, 400);

    // 右侧二维码按整数模块绘制，四周完整保留 4 模块白边。
    var qrY = Math.ceil(Math.max(104, nameBottom + 4));
    for (var r = 0; r < qr.modules.size; r++) {
      for (var c = 0; c < qr.modules.size; c++) {
        if (qr.modules.data[r * qr.modules.size + c]) {
          ctx.fillRect(qrX + (c + QUIET_MODULES) * moduleSize,
            qrY + (r + QUIET_MODULES) * moduleSize, moduleSize, moduleSize);
        }
      }
    }
    ctx.restore();
    resolve(canvas);
  });
}

// ================================================================
//  预览 / 打印
// ================================================================

/**
 * 生成物料标签预览图。
 * @param {Object} d - 标签数据
 * @returns {Promise<string>} PNG dataURL
 */
export function renderPreview(d) {
  return renderLabelCanvas(d).then(function(canvas) {
    return canvas.toDataURL("image/png");
  });
}

/** 当前预览顶部到第一行可打印黑点的距离，单位 mm。 */
export function getPreviewTopMarginMm(url) {
  return canvasFromPreview(url).then(function(canvas) {
    var pixels = canvas.getContext("2d").getImageData(0, 0, W, H).data;
    for (var y = 0; y < H; y += 1) {
      for (var x = 0; x < W; x += 1) {
        var index = (y * W + x) * 4;
        var lum = pixels[index] * 0.299 + pixels[index + 1] * 0.587 + pixels[index + 2] * 0.114;
        if (pixels[index + 3] > 128 && lum < 160) return y / DOTS_PER_MM;
      }
    }
    throw new Error("打印预览没有可打印内容");
  });
}

/**
 * 蓝牙打印物料标签；连接先于图片读取，保留用户点击手势。
 * @param {Object} d - 标签数据
 * @param {Object} [opts] - { previewUrl }，发送当前已显示的预览图
 * @returns {Promise}
 */
export function printMaterialBarcode(d, opts) {
  var options = opts || {};
  if (options.topShiftMm !== undefined && options.topShiftMm !== 0) {
    return Promise.reject(new Error("CC4 回退走纸补偿已停用，请刷新页面"));
  }
  return connectPrinter().then(function() {
    return options.previewUrl ? canvasFromPreview(options.previewUrl) : renderLabelCanvas(d);
  }).then(function(canvas) {
    return printCanvas(canvas, { protocol: "cpcl", media: "gap" });
  });
}
